import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, KeyRound, Smartphone, Mail, Loader2 } from "lucide-react";

export default function Security({ forced = false }) {
  const { user, refreshUser } = useAuth();
  const [method, setMethod] = useState("totp");
  const [setup, setSetup] = useState(null); // { method, qr, secret } | { method:'email' }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // change password (super admin)
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup", { method });
      setSetup(r.data);
      if (r.data.method === "email") toast.success(`Code sent to ${r.data.sent_to}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post(`/auth/2fa/enable?method=${setup.method}`, { code: code.trim() });
      toast.success("Two-factor authentication enabled");
      setSetup(null);
      setCode("");
      await refreshUser();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      if (user.twofa_method === "email") {
        await api.post("/auth/2fa/disable/request-code");
        toast.info("Enter the code we just emailed you to confirm.");
        setSetup({ disabling: true, method: "email" });
      } else {
        setSetup({ disabling: true, method: "totp" });
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: code.trim() });
      toast.success("Two-factor authentication disabled");
      setSetup(null);
      setCode("");
      await refreshUser();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  const enabled = user.twofa_enabled;
  const mandatory = user.twofa_required;
  const allowed = (user.twofa_allowed ?? true) || mandatory;
  const inputCls =
    "w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand";

  return (
    <div className={forced ? "max-w-lg w-full mx-auto" : "p-6 sm:p-8 max-w-2xl mx-auto"} data-testid="security-page">
      {!forced && (
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Account</div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">Security</h1>
        </div>
      )}

      {/* 2FA card */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 mb-6">
        <div className="flex items-start gap-3">
          {enabled ? (
            <ShieldCheck className="text-emerald-600 mt-0.5" size={22} />
          ) : (
            <ShieldAlert className={`${mandatory ? "text-red-600" : "text-zinc-400"} mt-0.5`} size={22} />
          )}
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900">
              Two-Factor Authentication
            </h2>
            <p className="text-sm text-zinc-500 mt-0.5" data-testid="twofa-status">
              {enabled
                ? `Enabled via ${user.twofa_method === "totp" ? "authenticator app" : "email code"}.`
                : mandatory
                ? "Required for your role. Please set it up now to secure your account."
                : !allowed
                ? "Two-factor authentication has been turned off for your account by an administrator."
                : "Add a second step at login for extra protection."}
            </p>
          </div>
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
              enabled ? "bg-emerald-100 text-emerald-800" : mandatory ? "bg-red-100 text-red-700" : !allowed ? "bg-zinc-200 text-zinc-500" : "bg-zinc-100 text-zinc-600"
            }`}
            data-testid="twofa-badge"
          >
            {enabled ? "On" : mandatory ? "Required" : !allowed ? "Unavailable" : "Off"}
          </span>
        </div>

        {/* Turned off by admin (optional roles only) */}
        {!enabled && !allowed && (
          <div className="mt-5 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-500" data-testid="twofa-disabled-notice">
            An administrator has turned off two-factor authentication for your account, so it can't be set up here. Contact your administrator if you need it enabled.
          </div>
        )}

        {/* Not enabled -> choose method + set up */}
        {!enabled && !setup && allowed && (
          <div className="mt-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMethod("totp")}
                data-testid="twofa-method-totp"
                className={`flex items-center gap-2 border p-3 text-sm font-semibold transition-colors ${
                  method === "totp" ? "border-brand bg-brand-card-blue" : "border-zinc-300 hover:bg-zinc-50"
                }`}
              >
                <Smartphone size={18} className="text-brand" /> Authenticator app
              </button>
              <button
                type="button"
                onClick={() => setMethod("email")}
                data-testid="twofa-method-email"
                className={`flex items-center gap-2 border p-3 text-sm font-semibold transition-colors ${
                  method === "email" ? "border-brand bg-brand-card-blue" : "border-zinc-300 hover:bg-zinc-50"
                }`}
              >
                <Mail size={18} className="text-brand" /> Email code
              </button>
            </div>
            <button
              onClick={startSetup}
              disabled={busy}
              data-testid="twofa-setup-button"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2 disabled:opacity-60"
            >
              {busy && <Loader2 className="animate-spin" size={16} />} Set up
            </button>
          </div>
        )}

        {/* Setup in progress */}
        {setup && !setup.disabling && (
          <div className="mt-5 space-y-4">
            {setup.method === "totp" && (
              <div>
                <p className="text-sm text-zinc-600 mb-2">
                  Scan this QR code with Google Authenticator, Microsoft Authenticator or Authy, then enter the 6-digit code.
                </p>
                <img src={setup.qr} alt="2FA QR code" data-testid="twofa-qr" className="h-44 w-44 border border-zinc-200" />
                <p className="text-xs text-zinc-400 mt-2 break-all">
                  Or enter this key manually: <span className="font-mono text-zinc-600">{setup.secret}</span>
                </p>
              </div>
            )}
            {setup.method === "email" && (
              <p className="text-sm text-zinc-600">
                We emailed a 6-digit code to <b>{user.email}</b>. Enter it below to finish enabling 2FA.
              </p>
            )}
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="twofa-code-input"
              placeholder="123456"
              inputMode="numeric"
              className={inputCls + " tracking-[0.3em] font-mono"}
            />
            <div className="flex gap-3">
              <button
                onClick={enable}
                disabled={busy || !code.trim()}
                data-testid="twofa-enable-button"
                className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2 disabled:opacity-60"
              >
                {busy && <Loader2 className="animate-spin" size={16} />} Verify & enable
              </button>
              <button onClick={() => { setSetup(null); setCode(""); }} className="text-sm font-bold text-zinc-500 hover:text-zinc-900">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Disable confirmation */}
        {setup && setup.disabling && (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-zinc-600">
              Enter your current {user.twofa_method === "totp" ? "authenticator" : "email"} code to disable 2FA.
            </p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="twofa-disable-code-input"
              placeholder="123456"
              inputMode="numeric"
              className={inputCls + " tracking-[0.3em] font-mono"}
            />
            <div className="flex gap-3">
              <button
                onClick={confirmDisable}
                disabled={busy || !code.trim()}
                data-testid="twofa-confirm-disable-button"
                className="bg-red-600 text-white rounded-full font-bold tracking-tight hover:bg-red-700 transition-colors px-5 py-2.5 disabled:opacity-60"
              >
                Disable 2FA
              </button>
              <button onClick={() => { setSetup(null); setCode(""); }} className="text-sm font-bold text-zinc-500 hover:text-zinc-900">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Enabled -> disable (only if optional) */}
        {enabled && !setup && !mandatory && (
          <button
            onClick={disable}
            disabled={busy}
            data-testid="twofa-disable-button"
            className="mt-5 border border-zinc-300 text-zinc-800 rounded-full font-bold tracking-tight hover:bg-zinc-50 transition-colors px-5 py-2.5"
          >
            Disable 2FA
          </button>
        )}
      </div>

      {/* Change password — available to all roles */}
      {(
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6" data-testid="change-password-card">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={20} className="text-brand" />
            <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900">Change Password</h2>
          </div>
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Current password</label>
              <input type="password" required value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="cp-current" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">New password</label>
                <input type="password" required minLength={6} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="cp-new" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Confirm new</label>
                <input type="password" required minLength={6} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="cp-confirm" className={inputCls} />
              </div>
            </div>
            <button
              type="submit"
              disabled={pwBusy}
              data-testid="cp-submit"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2.5 flex items-center gap-2 disabled:opacity-60"
            >
              {pwBusy && <Loader2 className="animate-spin" size={16} />} Update password
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
